/**
 * The asset's own fields, shared by New asset and the editor: name, kind, tagline, summary, maturity, home country,
 * industries (approved and active, plus **Suggest an industry**), partners (free names, suggesting those in use),
 * countries and experts. Controlled: the caller holds the draft and saves it.
 */

import { useState } from 'react';
import { Lightbulb } from 'lucide-react';

import {
  assetCreateInNameMax,
  assetCreateInPartnersOneItemMax,
  assetCreateInSummaryOneMax,
  assetCreateInTaglineOneMax,
} from '@/api/generated/schemas';
import { useCountries, useIndustries, usePartners } from '@/api/queries';
import type { AssetMaturity, Tag } from '@/api/types';
import type { FieldErrors } from '@/api/validate';
import { useSession } from '@/session/SessionContext';
import { FieldError, fieldA11y } from '@/ui/FieldError';
import { LabelInput } from '@/ui/LabelInput';
import { MultiSelect } from '@/ui/MultiSelect';

import { KIND_HINTS, KIND_OPTIONS, MATURITY_OPTIONS } from '../assetUi';
import type { AssetFieldsDraft } from './assetFields';
import { ExpertPicker } from './ExpertPicker';
import { SuggestIndustryDialog } from './SuggestIndustryDialog';

type Patch = (patch: Partial<AssetFieldsDraft>) => void;

/** The fields that show a 422 message beside them, by path, with their inputs' ids; the page's banner says the rest. */
const INPUT_IDS = {
  name: 'asset-name',
  kind: 'asset-kind',
  tagline: 'asset-tagline',
  summary: 'asset-summary',
  partners: 'asset-partners',
} as const satisfies Partial<Record<keyof AssetFieldsDraft, string>>;
export const FIELD_PATHS = Object.keys(INPUT_IDS);

/** Options, keeping an industry the asset already has even when the list no longer offers it. */
function withCurrent(options: Tag[], current: Tag[]) {
  const known = new Set(options.map((o) => o.id));
  return [...options, ...current.filter((c) => !known.has(c.id))].map((t) => ({ value: t.id, label: t.name }));
}

function TextFields({ draft, set, errors }: { draft: AssetFieldsDraft; set: Patch; errors: FieldErrors }) {
  return (
    <>
      <div className="field">
        <label htmlFor={INPUT_IDS.name}>Name</label>
        <input id={INPUT_IDS.name} type="text" required maxLength={assetCreateInNameMax} value={draft.name} {...fieldA11y(INPUT_IDS.name, errors.name)} onChange={(e) => set({ name: e.target.value })} />
        <FieldError inputId={INPUT_IDS.name} message={errors.name} />
      </div>
      <div className="field">
        <label htmlFor={INPUT_IDS.kind}>Kind</label>
        <select id={INPUT_IDS.kind} required value={draft.kind} {...fieldA11y(INPUT_IDS.kind, errors.kind, 'asset-kind-hint')} onChange={(e) => set({ kind: e.target.value as AssetFieldsDraft['kind'] })}>
          <option value="" disabled>Choose a kind</option>
          {KIND_OPTIONS.map((k) => <option key={k.value} value={k.value}>{k.label}</option>)}
        </select>
        <p id="asset-kind-hint" className="hint">{draft.kind ? KIND_HINTS[draft.kind] : 'The kind decides the suggested sections.'}</p>
        <FieldError inputId={INPUT_IDS.kind} message={errors.kind} />
      </div>
      <div className="field span-2">
        <label htmlFor={INPUT_IDS.tagline}>Tagline</label>
        <input id={INPUT_IDS.tagline} type="text" maxLength={assetCreateInTaglineOneMax} value={draft.tagline} {...fieldA11y(INPUT_IDS.tagline, errors.tagline, 'asset-tagline-count')} onChange={(e) => set({ tagline: e.target.value })} />
        <p id="asset-tagline-count" className="hint" aria-live="polite">{draft.tagline.length} of {assetCreateInTaglineOneMax} characters</p>
        <FieldError inputId={INPUT_IDS.tagline} message={errors.tagline} />
      </div>
      <div className="field span-2">
        <label htmlFor={INPUT_IDS.summary}>Summary</label>
        <textarea id={INPUT_IDS.summary} rows={4} maxLength={assetCreateInSummaryOneMax} value={draft.summary} {...fieldA11y(INPUT_IDS.summary, errors.summary, 'asset-summary-count')} onChange={(e) => set({ summary: e.target.value })} />
        <p id="asset-summary-count" className="hint" aria-live="polite">{draft.summary.length} of {assetCreateInSummaryOneMax} characters</p>
        <FieldError inputId={INPUT_IDS.summary} message={errors.summary} />
      </div>
    </>
  );
}

function PlaceFields({ draft, set }: { draft: AssetFieldsDraft; set: Patch }) {
  const countries = useCountries();
  return (
    <>
      <div className="field">
        <label htmlFor="asset-maturity">Maturity</label>
        <select id="asset-maturity" value={draft.maturity} onChange={(e) => set({ maturity: e.target.value as AssetMaturity })}>
          {MATURITY_OPTIONS.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
        </select>
      </div>
      <div className="field">
        <label htmlFor="asset-home">Home country</label>
        <select id="asset-home" value={draft.home_country} onChange={(e) => set({ home_country: e.target.value })}>
          {(countries.data ?? [{ code: draft.home_country, name: draft.home_country }]).map((c) => (
            <option key={c.code} value={c.code}>{c.name}</option>
          ))}
        </select>
      </div>
      <div className="field span-2">
        <span className="label">Available in</span>
        <MultiSelect
          label="Countries where it is available"
          allLabel="No countries chosen"
          noun="countries"
          searchable
          options={(countries.data ?? []).map((c) => ({ value: c.code, label: c.name }))}
          value={draft.countries}
          onChange={(list) => set({ countries: list })}
        />
      </div>
    </>
  );
}

function TagFields({ draft, set, current, errors }: { draft: AssetFieldsDraft; set: Patch; current: { industries: Tag[] }; errors: FieldErrors }) {
  const { can } = useSession();
  const industries = useIndustries({ enabled: can('industry.view') });
  const partners = usePartners();
  const [suggesting, setSuggesting] = useState(false);
  const approved = (industries.data?.industries ?? []).filter((i) => i.status === 'approved' && i.is_active);
  return (
    <>
      <div className="field">
        <span className="label">Industries</span>
        <MultiSelect label="Industries" allLabel="No industries chosen" noun="industries" searchable options={withCurrent(approved, current.industries)} value={draft.industry_ids} onChange={(list) => set({ industry_ids: list })} />
        {can('industry.suggest') && (
          <button type="button" className="btn btn-ghost btn-sm" onClick={() => setSuggesting(true)}>
            <Lightbulb aria-hidden="true" /> Suggest an industry
          </button>
        )}
      </div>
      <div className="field">
        <label htmlFor={INPUT_IDS.partners}>Partners and hyperscalers</label>
        <LabelInput
          id={INPUT_IDS.partners}
          error={errors.partners}
          describedBy="asset-partners-hint"
          maxLength={assetCreateInPartnersOneItemMax}
          placeholder="Type a partner"
          suggestions={(partners.data?.partners ?? []).map((p) => p.name)}
          value={draft.partners}
          onChange={(list) => set({ partners: list })}
        />
        <p id="asset-partners-hint" className="hint">Press Enter or type a comma after each name. A new name is added as a partner.</p>
      </div>
      {suggesting && (
        <SuggestIndustryDialog
          onClose={() => setSuggesting(false)}
          onSuggested={(industry) => {
            if (industry.status === 'approved' && industry.is_active && !draft.industry_ids.includes(industry.id)) {
              set({ industry_ids: [...draft.industry_ids, industry.id] });
            }
          }}
        />
      )}
    </>
  );
}

export function AssetFieldsForm({
  draft,
  set,
  current = { industries: [] },
  errors = {},
}: {
  draft: AssetFieldsDraft;
  set: Patch;
  /** The saved asset's industries, so one no longer offered keeps its name. */
  current?: { industries: Tag[] };
  /** The server's 422 messages by field, shown beside each. */
  errors?: FieldErrors;
}) {
  return (
    <div className="stack-md">
      <div className="grid grid-2 asset-fields">
        <TextFields draft={draft} set={set} errors={errors} />
        <PlaceFields draft={draft} set={set} />
        <TagFields draft={draft} set={set} current={current} errors={errors} />
      </div>
      <ExpertPicker experts={draft.experts} onChange={(experts) => set({ experts })} />
    </div>
  );
}
